import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, BookOpen, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface WikiArticle {
  title: string
  slug: string
  category: 'architecture' | 'data' | 'operations' | 'guides'
  lastUpdated: string
  content: string
}

const CATEGORY_LABELS: Record<string, string> = {
  architecture: 'Architecture',
  data: 'Data Sources',
  operations: 'Operations',
  guides: 'Guides',
}

const CATEGORY_ORDER = ['architecture', 'data', 'operations', 'guides']

const ARTICLES: WikiArticle[] = [
  {
    title: 'Platform Architecture',
    slug: 'platform-architecture',
    category: 'architecture',
    lastUpdated: '2026-04-10',
    content: `**Overview**

The CTO Dashboard is a full-stack intelligence platform built with a React + TypeScript frontend and a Python FastAPI backend.

**Frontend Stack**
- React 18 with TypeScript
- Vite for bundling and dev server
- Tailwind CSS with a custom dark theme
- Recharts and Lucide icons
- Axios for API communication

**Backend Stack**
- Python 3.11+ with FastAPI
- SQLite for persistent storage
- APScheduler for background data collection
- JWT authentication with MFA support

**Key Design Decisions**
- Single-page application with client-side routing
- All data fetched via REST API endpoints
- Background schedulers collect and aggregate data automatically
- Dark-first UI with consistent card-based layouts
- Role-based access control (admin, editor, viewer)`,
  },
  {
    title: 'Data Pipeline',
    slug: 'data-pipeline',
    category: 'architecture',
    lastUpdated: '2026-04-08',
    content: `**Data Collection Architecture**

The platform collects intelligence from 150+ sources using a multi-stage pipeline.

**Stage 1 — Ingestion**
- RSS feed polling every 15 minutes
- API integrations for structured data (NVD, cloud status pages)
- Web scraping for sources without feeds
- Discord webhook listeners

**Stage 2 — Processing**
- NLP-based categorisation and entity extraction
- Sentiment analysis on article content
- Trend detection via rolling window algorithms
- Deduplication using fuzzy title matching

**Stage 3 — Storage & Indexing**
- SQLite with full-text search indexes
- 30-day rolling retention for raw articles
- Aggregated metrics retained indefinitely
- Cached API responses for performance

**Stage 4 — Delivery**
- REST API endpoints for frontend consumption
- Discord alerts for critical signals
- Daily briefing generation`,
  },
  {
    title: 'RSS Feed Sources',
    slug: 'rss-feed-sources',
    category: 'data',
    lastUpdated: '2026-04-12',
    content: `**Feed Categories**

The platform monitors RSS feeds across multiple categories to provide comprehensive tech intelligence.

**Tech News (40+ feeds)**
- TechCrunch, Ars Technica, The Verge
- Hacker News (top stories and best)
- MIT Technology Review
- Wired, ZDNet, The Register

**Security & Cyber (30+ feeds)**
- CISA advisories and alerts
- NVD vulnerability feeds
- Krebs on Security, Schneier on Security
- BleepingComputer, The Hacker News
- Threat intelligence vendor blogs

**Cloud & Infrastructure (20+ feeds)**
- AWS, Azure, GCP status and blogs
- Cloudflare blog and radar
- Kubernetes blog and CNCF updates

**AI & Research (25+ feeds)**
- arXiv CS selections
- Google AI, Meta AI, OpenAI blogs
- Hugging Face blog
- Papers With Code highlights

**Open Source & Dev (20+ feeds)**
- GitHub trending and blog
- Dev.to, Changelog
- Language and framework release feeds

**Business & Strategy (15+ feeds)**
- Gartner, Forrester highlights
- CB Insights, Crunchbase News
- McKinsey Technology, HBR Technology`,
  },
  {
    title: 'Trend Mining Engine',
    slug: 'trend-mining-engine',
    category: 'data',
    lastUpdated: '2026-04-06',
    content: `**How Trends Are Detected**

The trend mining engine analyses article frequency, sentiment shifts, and entity co-occurrence to surface emerging technology trends.

**Algorithm Overview**
- Rolling 7-day and 30-day mention frequency windows
- Z-score anomaly detection for spike identification
- Exponential moving average for trend direction
- Category-weighted scoring (security signals weighted higher)

**Trend Lifecycle**
- \`emerging\` — First detected, mentions accelerating
- \`growing\` — Sustained growth over 7+ days
- \`peaking\` — Maximum velocity reached
- \`declining\` — Mentions decreasing from peak
- \`stable\` — Consistent baseline mentions

**Entity Extraction**
- Named entity recognition for companies, products, technologies
- Custom dictionary of 500+ tech-specific terms
- Co-occurrence mapping to detect related trends

**Hype Cycle Mapping**
- Articles are mapped to a simplified Gartner-style hype cycle
- Position estimated from sentiment trajectory and mention velocity
- Five phases: trigger, peak, trough, slope, plateau`,
  },
  {
    title: 'Discord Integration',
    slug: 'discord-integration',
    category: 'operations',
    lastUpdated: '2026-04-11',
    content: `**Discord Alert System**

The platform sends real-time alerts to Discord channels using webhooks.

**Alert Categories**
- Critical CVEs (CVSS 9.0+) — immediate notification
- Trending tech signals — hourly digest
- Cloud provider incidents — real-time status changes
- Daily intelligence briefing — scheduled summary

**Webhook Configuration**
- Webhooks configured via environment variables
- Separate channels for security, trends, and briefings
- Rate limiting to avoid Discord API throttling (30 messages/minute)

**Message Formatting**
- Rich embeds with colour-coded severity
- Inline links to source articles
- Mention counts and trend indicators
- Actionable summaries with key takeaways

**Commands**
- Webhook URLs stored in \`DISCORD_WEBHOOK_URL\` env var
- Multiple webhooks supported via comma separation
- Test endpoint at \`/api/discord/test\`
- Alert history viewable in the Integrations page`,
  },
  {
    title: 'Deployment Guide',
    slug: 'deployment-guide',
    category: 'guides',
    lastUpdated: '2026-04-09',
    content: `**Deploying the CTO Dashboard**

**Prerequisites**
- Python 3.11+
- Node.js 18+
- Git

**Local Development**
\`\`\`
# Backend
cd backend
pip install -r requirements.txt
uvicorn main:app --reload --port 8000

# Frontend
cd frontend
npm install
npm run dev
\`\`\`

**Production Deployment**
- Frontend built with \`npm run build\`, served as static files
- Backend runs via uvicorn behind a reverse proxy
- Caddy recommended as reverse proxy (auto HTTPS)
- SQLite database stored in \`/data\` volume

**Environment Variables**
- \`JWT_SECRET\` — Secret key for token signing
- \`DISCORD_WEBHOOK_URL\` — Discord notification webhooks
- \`ADMIN_EMAIL\` — Initial admin account email
- \`DATA_DIR\` — Path to persistent data directory

**Docker**
\`\`\`
docker build -t cto-dashboard .
docker run -p 3000:3000 -v data:/data cto-dashboard
\`\`\`

**Health Check**
- \`GET /api/health\` returns system status
- Includes uptime, feed count, last sync time`,
  },
  {
    title: 'Cyber Threat Sources',
    slug: 'cyber-threat-sources',
    category: 'data',
    lastUpdated: '2026-04-13',
    content: `**Cyber Intelligence Sources**

The platform aggregates threat intelligence from multiple authoritative sources.

**Vulnerability Databases**
- NVD (National Vulnerability Database) — full CVE feed
- CISA Known Exploited Vulnerabilities catalogue
- GitHub Security Advisories
- OSV (Open Source Vulnerabilities)

**Threat Intelligence**
- MITRE ATT&CK updates
- AlienVault OTX pulse feed
- Abuse.ch threat feeds (URLhaus, MalwareBazaar)
- PhishTank verified phishing URLs

**Vendor Advisories**
- Microsoft Security Response Center
- Google Project Zero disclosures
- Apple security updates
- Linux kernel security notices

**Processing**
- CVEs enriched with CVSS scores, affected products, exploit availability
- Automatic matching against watchlist technologies
- Severity-based prioritisation (Critical > High > Medium > Low)
- Exploit prediction scoring for prioritisation

**Alerting Thresholds**
- CVSS 9.0+ triggers immediate Discord alert
- CVSS 7.0+ included in daily briefing
- Known exploited vulnerabilities always flagged
- Watchlist matches elevated by one severity level`,
  },
  {
    title: 'Cloud Monitoring',
    slug: 'cloud-monitoring',
    category: 'operations',
    lastUpdated: '2026-04-14',
    content: `**Cloud Provider Monitoring**

The platform monitors major cloud providers for status changes, pricing updates, and new service announcements.

**Providers Tracked**
- AWS (all regions) — status.aws.amazon.com
- Microsoft Azure — status.azure.com
- Google Cloud Platform — status.cloud.google.com
- Cloudflare — cloudflarestatus.com
- Vercel, Netlify, Fly.io

**Status Monitoring**
- Polling every 5 minutes for status page changes
- Historical incident tracking with timeline
- Region-level granularity for major providers
- Service-level status (compute, storage, networking, database)

**Pricing Intelligence**
- Daily price scraping for common instance types
- Cross-provider comparison tables
- Price change detection and alerts
- Reserved vs on-demand vs spot tracking

**Service Announcements**
- New service and feature launches tracked
- GA, preview, and deprecation notices
- Region expansion notifications
- SDK and API version updates

**Dashboard Widgets**
- Current status overview with traffic-light indicators
- Incident timeline with severity and affected services
- Price comparison charts across providers
- Service availability heatmaps by region`,
  },
]

function renderContent(content: string) {
  const lines = content.split('\n')
  return lines.map((line, i) => {
    const trimmed = line.trimStart()

    // Code block lines
    if (trimmed.startsWith('```')) {
      return null // skip delimiters
    }

    // Bold headers
    if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      return (
        <h3 key={i} className="text-sm font-bold text-text-primary mt-4 mb-1">
          {trimmed.replace(/\*\*/g, '')}
        </h3>
      )
    }

    // Bullet list items
    if (trimmed.startsWith('- ')) {
      const text = trimmed.slice(2)
      // Handle inline bold
      const parts = text.split(/(\*\*[^*]+\*\*)/)
      return (
        <div key={i} className="flex gap-2 text-xs text-text-secondary ml-2 leading-relaxed">
          <span className="text-text-muted select-none">•</span>
          <span>
            {parts.map((p, j) =>
              p.startsWith('**') && p.endsWith('**')
                ? <span key={j} className="font-semibold text-text-primary">{p.replace(/\*\*/g, '')}</span>
                : <span key={j}>{p}</span>
            )}
          </span>
        </div>
      )
    }

    // Inline code / code block content (lines inside ```)
    if (trimmed.startsWith('`') && trimmed.endsWith('`') && !trimmed.startsWith('``')) {
      return (
        <code key={i} className="block text-[11px] font-mono text-cyan bg-surface-alt px-3 py-0.5 rounded">
          {trimmed.replace(/`/g, '')}
        </code>
      )
    }

    // Check if we're in a code block context (simple heuristic: lines that look like commands)
    if (/^(cd |pip |npm |uvicorn |docker |git |GET |POST )/.test(trimmed) || (trimmed.includes('=') && !trimmed.includes(' ') && trimmed.length < 60)) {
      return (
        <code key={i} className="block text-[11px] font-mono text-cyan bg-surface-alt px-3 py-0.5 rounded">
          {trimmed}
        </code>
      )
    }

    // Empty line
    if (!trimmed) return <div key={i} className="h-2" />

    // Regular paragraph with inline bold
    const parts = trimmed.split(/(\*\*[^*]+\*\*)/)
    return (
      <p key={i} className="text-xs text-text-secondary leading-relaxed">
        {parts.map((p, j) =>
          p.startsWith('**') && p.endsWith('**')
            ? <span key={j} className="font-semibold text-text-primary">{p.replace(/\*\*/g, '')}</span>
            : <span key={j}>{p}</span>
        )}
      </p>
    )
  })
}

export default function WikiPage() {
  const [articles] = useState<WikiArticle[]>(ARTICLES)
  const [selectedSlug, setSelectedSlug] = useState(ARTICLES[0].slug)
  const [search, setSearch] = useState('')

  const filtered = articles.filter(a =>
    !search || a.title.toLowerCase().includes(search.toLowerCase()) ||
    a.content.toLowerCase().includes(search.toLowerCase())
  )

  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    label: CATEGORY_LABELS[cat],
    items: filtered.filter(a => a.category === cat),
  })).filter(g => g.items.length > 0)

  const selected = articles.find(a => a.slug === selectedSlug) || articles[0]

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <BookOpen size={20} className="text-blue" /> Knowledge Base
          </h1>
          <p className="text-[10px] text-text-secondary">Built-in documentation covering platform architecture, data sources, and operations</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search articles..."
          className="input py-2 text-xs pl-9 w-full"
        />
      </div>

      {/* Two-column layout */}
      <div className="flex gap-4" style={{ minHeight: 'calc(100vh - 200px)' }}>
        {/* Sidebar */}
        <div className="w-64 shrink-0 space-y-3">
          {grouped.map(g => (
            <div key={g.category}>
              <h3 className="text-[10px] font-semibold text-text-muted uppercase tracking-wider mb-1 px-2">
                {g.label}
              </h3>
              <div className="space-y-0.5">
                {g.items.map(a => (
                  <button
                    key={a.slug}
                    type="button"
                    onClick={() => setSelectedSlug(a.slug)}
                    className={clsx(
                      'w-full text-left px-3 py-2 rounded-lg text-xs transition-colors',
                      a.slug === selectedSlug
                        ? 'bg-blue/15 text-blue font-medium'
                        : 'text-text-secondary hover:bg-surface-alt hover:text-text-primary'
                    )}
                  >
                    {a.title}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 card p-6 overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-lg font-bold text-text-primary">{selected.title}</h2>
            <span className="text-[10px] text-text-muted shrink-0 ml-4">
              Updated {selected.lastUpdated}
            </span>
          </div>
          <span className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue/15 text-blue mb-4">
            {CATEGORY_LABELS[selected.category]}
          </span>
          <div className="space-y-0.5">
            {renderContent(selected.content)}
          </div>
        </div>
      </div>
    </div>
  )
}
